import { useLocation, useNavigate } from "react-router-dom";
import { Settings, UserCircle2, ClipboardCheck, MapPin } from "lucide-react";
import { NAV_GROUPS } from "@/components/Sidebar";
import { GlobalSearchInline } from "@/components/CommandPalette";
import { DotseroButton } from "@/components/DotseroButton";
import { ExternalLink } from "lucide-react";
import { useI18n } from "@/i18n";

// Routes that are not sidebar items but still get a header. Checked after the nav items; longest prefix wins.
const EXTRA = [
  { prefix: "/settings", label: "Settings", icon: Settings },
  { prefix: "/profile", label: "My Profile", icon: UserCircle2 },
  { prefix: "/inspections", label: "Inspection", icon: ClipboardCheck },
  { prefix: "/sites", label: "Sites", icon: MapPin },
];

export function resolvePage(pathname) {
  const path = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
  const items = NAV_GROUPS.flatMap((g) => g.items);
  const exact = items.find((i) => i.path === path);
  if (exact) return exact;
  const byPrefix = items.filter((i) => i.path !== "/" && path.startsWith(`${i.path}/`)).sort((a, b) => b.path.length - a.path.length)[0];
  if (byPrefix) return byPrefix;
  return EXTRA.find((e) => path === e.prefix || path.startsWith(`${e.prefix}/`)) || null;
}

// 64px dark page header — icon + page name on the left, global Quick Search (⌘K) on the right. Desktop only.
export default function PageHeader({ onOpenPalette }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const page = resolvePage(pathname);
  if (!page) return null;
  const Icon = page.icon;
  const home = page.path || page.prefix || "/";
  return (
    <header className="abs-page-header hidden md:flex h-16 shrink-0 items-center px-4" data-testid="page-header">
      {/* Icon + name = "take me to this section's main view" (clears tabs, filters and deep links). */}
      <button type="button" onClick={() => navigate(home)} title={t("Back to {page}", { page: t(page.label) })} data-testid="page-header-home"
        className="inline-flex items-center gap-2.5 rounded-md px-1 -mx-1 hover:bg-white/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40">
        {Icon && <Icon className="h-5 w-5 text-slate-400" aria-hidden="true" />}
        <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }} data-testid="page-header-title">
          {t(page.title || page.label)}
        </h1>
      </button>
      {onOpenPalette && <GlobalSearchInline />}
      <button type="button" onClick={() => window.open(window.location.href, "_blank", "noopener")}
        title={t("Open this page in a new tab")} aria-label={t("Open this page in a new tab")}
        className="mr-2 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/10 text-slate-400 transition-colors hover:border-white/25 hover:text-slate-100"
        data-testid="open-new-tab-button">
        <ExternalLink className="h-4 w-4" />
      </button>
      <DotseroButton />
    </header>
  );
}
